import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { claimsAtom } from '../stores/auth/atoms';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { AgentProcessLibrary } from './AgentProcessesView';
import { AgentLibrary } from './AgentLibraryView';
import { AgentRoster } from '../components/agents/AgentRoster';
import { useMcpServersPanel } from '../components/mcp/McpServersPanel';
import { useProviders, useProviderModels, useSetProviderParams, useSyncCatalog, useVisionModel } from '../hooks/useProviderManagement';
import { useIsOrgAdmin } from '../hooks/useAuthManagement';
import { usePlatform } from '@localagent/platform';
import { ModelEntryDialog, type ModelEntryDialogMode } from '../components/providers/ModelEntryDialog';
import { ModelDefaultsDialog } from '../components/providers/ModelDefaultsDialog';
import { ProviderDefaultsDialog } from '../components/providers/ProviderDefaultsDialog';
import { DeleteEntryDialog } from '../components/providers/DeleteEntryDialog';
import { ProviderDiscoverDialog } from '../components/providers/ProviderDiscoverDialog';
import { ParamSpecEditor } from '../components/providers/ParamSpecEditor';
import { ProviderKeySection } from '../components/providers/ProviderKeySection';
import { VisionModelNotice } from '../components/providers/VisionModelNotice';
import { ContextTab } from '../components/context/ContextTab';
import { SkillsTab } from '../components/agents/SkillsTab';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { ListDetailLayout } from '@localagent/ui/components/list-detail-layout';
import { DataList } from '@localagent/ui/components/data-list';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@localagent/ui/components/tabs';
import { PageHeaderBar } from '../components/layout/PageHeaderBar';
import { PAGE_TABS_LIST_CLASS, PAGE_TABS_TRIGGER_CLASS } from '../components/layout/PageTabs';
import { Input } from '@localagent/ui/components/input';
import { toast } from '@localagent/ui/components/sonner';
import { useFilteredList } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { cn } from '@localagent/ui/lib/utils';
import type { ProviderInfo, ModelCatalogEntry, SupportedParamSpec, SyncResult } from '@localagent/sdk';
import { ModelCapabilityPills, ModelModalities, ModelPricingCell, ModelStatusBadge } from '../components/providers/ModelCatalogCells';

// Keep in sync with the TabsTrigger values below. Used to reject an unknown
// `?tab=` rather than adopting it and rendering an empty view.
const AGENTS_VIEW_TABS = ['personas', 'agents', 'tools', 'providers', 'processes', 'context', 'skills'];

const EMPTY_PROVIDER_PARAMS: Record<string, SupportedParamSpec> = {};

const providerSearchFn = (item: ProviderInfo, query: string): boolean => {
  const q = query.toLowerCase();
  return item.provider_type.toLowerCase().includes(q);
};

const modelSearchFn = (entry: ModelCatalogEntry, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    (entry.display_name?.toLowerCase().includes(q) ?? false) ||
    entry.name.toLowerCase().includes(q) ||
    entry.model_id.toLowerCase().includes(q)
  );
};

function formatLabel(value: string): string {
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function credentialBadge(provider: ProviderInfo) {
  if (provider.credential_type === 'device') {
    return <Badge variant="outline" className="text-micro">Device</Badge>;
  }
  if (provider.credential_type === 'none') {
    return <Badge variant="outline" className="text-micro">Keyless</Badge>;
  }
  return provider.credential_configured
    ? <Badge variant="success" className="text-micro">Configured</Badge>
    : <Badge variant="warning" className="text-micro">No API key</Badge>;
}

function originBadge(entry: ModelCatalogEntry) {
  return (
    <Badge
      variant={entry.origin === 'custom' ? 'secondary' : 'outline'}
      className="text-micro"
      title={entry.origin === 'synced' ? 'Sync resets this entry' : undefined}
    >
      {formatLabel(entry.origin)}
    </Badge>
  );
}

// Catalog tier. `synced` entries are read-only mirrors of the seeded GLOBAL
// master (edit by Copy-ing into the account); `custom`/`discovered` are managed
// within this ACCOUNT. Mirrors the Personas Templates (read-only) vs My Agents
// (owned) split, unified here into one filtered list.
type ModelTier = 'global' | 'account';
function entryTier(entry: ModelCatalogEntry): ModelTier {
  return entry.origin === 'synced' ? 'global' : 'account';
}


export function AgentsView() {
  useCatalogSubscription('persona');
  useCatalogSubscription('skills');
  useCatalogSubscription('agentProcesses');
  useCatalogSubscription('context');
  // The agent-process library is a tab here (moved off the left nav in round-2
  // UX). The tab is URL-addressable via `?tab=processes` so the standalone
  // editor routes can navigate back to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const liveSearchParams = useRef(searchParams);
  liveSearchParams.current = searchParams;

  // URL state has exactly one writer, and it is never an effect (app-shell ADR-006).
  const writeUrl = useCallback((mutate: (next: URLSearchParams) => void) => {
    const current = liveSearchParams.current;
    const next = new URLSearchParams(current);
    mutate(next);
    if (next.toString() === current.toString()) return;
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  // An unrecognized `?tab=` used to be adopted verbatim, so a stale link —
  // `?tab=injections`, which the retired Instructions tab minted and users may
  // still have bookmarked — selected a tab with no panel and rendered a BLANK
  // view with no error and no way back except editing the URL. Falling back to
  // the default turns a dead link into a working page.
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && AGENTS_VIEW_TABS.includes(requestedTab)
    ? requestedTab
    : 'personas';

  const handleTabChange = useCallback((tab: string) => {
    writeUrl(next => {
      if (tab === 'personas') next.delete('tab');
      else next.set('tab', tab);
      // Provider/model selection is scoped to the Providers tab; leaving it in
      // the URL after navigating away deep-links back into a stale selection.
      if (tab !== 'providers') {
        next.delete('provider');
        next.delete('model');
        next.delete('reassign');
      }
    });
  }, [writeUrl]);

  const mcpPanel = useMcpServersPanel();
  const selectMcp = mcpPanel.setSelectedId;

  // Deep-linkable provider selection: /agents?tab=providers&provider=fireworks
  // lands on the Providers tab with Fireworks selected, and &model= expands that row.
  const providerSelectedId = activeTab === 'providers' ? searchParams.get('provider') : null;
  // Row expander: the provenance/pricing/modality detail that previously
  // rendered ONLY in the transient discover dialog and vanished on import
  // One row at a time — a table of always-open detail rows is
  // unreadable at 36 OpenAI models.
  const expandedModel = activeTab === 'providers' ? searchParams.get('model') : null;

  const [entryDialogMode, setEntryDialogMode] = useState<ModelEntryDialogMode | null>(null);
  const [entryDialogSource, setEntryDialogSource] = useState<ModelCatalogEntry | undefined>(undefined);
  const [deleteEntryTarget, setDeleteEntryTarget] = useState<ModelCatalogEntry | null>(null);
  // The entry whose DEFAULT VALUES are being edited — distinct from
  // `entryDialogSource`, which is the entry whose capability SPEC is being
  // edited. Keeping them apart in state is the same split the routes make.
  const [defaultsTarget, setDefaultsTarget] = useState<ModelCatalogEntry | null>(null);
  const [providerDefaultsOpen, setProviderDefaultsOpen] = useState(false);
  const [providerParamsDraft, setProviderParamsDraft] = useState<Record<string, SupportedParamSpec>>({});
  const [providerSchemaError, setProviderSchemaError] = useState('');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [discoverDialogOpen, setDiscoverDialogOpen] = useState(false);
  const [discoverSummary, setDiscoverSummary] = useState<string | null>(null);


  // Providers hooks
  const { data: providersData, isLoading: providersLoading, error: providersError } = useProviders();
  const {
    data: visionModelInfo,
    isLoading: visionModelLoading,
    error: visionModelError,
  } = useVisionModel();

  const { data: providerModelsData } = useProviderModels(providerSelectedId);
  const setProviderParamsMutation = useSetProviderParams();
  const syncCatalogMutation = useSyncCatalog();
  const platform = usePlatform();
  // Providers tab: which catalog tier the model list shows (global read-only /
  // account-managed / all). Mirrors the Personas Templates-vs-My-Agents filter.
  const [modelTier, setModelTier] = useState<'all' | ModelTier>('all');

  // Deep-link from the agent editor's "Find a replacement" CTA:
  // `/agents?tab=providers&provider=X&model=Y&reassign=1` opens the same
  // DeleteEntryDialog reassign flow with the row in focus.
  const reassignRequested = searchParams.get('reassign');
  const reassignModelName = searchParams.get('model');
  useEffect(() => {
    if (activeTab !== 'providers') return;
    if (reassignRequested !== '1') return;
    if (!reassignModelName || !providerModelsData?.models) return;
    const entry = providerModelsData.models.find(m => m.name === reassignModelName);
    if (!entry) return;
    setDeleteEntryTarget(entry);
    writeUrl(next => { next.delete('reassign'); });
  }, [activeTab, reassignRequested, reassignModelName, providerModelsData, writeUrl]);


  // Providers tab derived data
  const providers = useMemo(() => providersData?.providers ?? [], [providersData]);

  // The Provider Key section is admin-only (the credential
  // routes 403 non-admins). The session carries no roles
  // (account ADR-020 / authz ADR-007) — a JWT-claim check here is always
  // false for every real user (authz ADR-007's own documented production
  // bug). Gate on the server's own answer instead, via the same
  // probe-and-403 idiom RoleManagementSection uses for the account tier.
  const claims = useAtomValue(claimsAtom);
  const { isOrgAdmin: isAccountAdmin } = useIsOrgAdmin(claims?.orgId);

  const selectedProvider = useMemo(
    () => providers.find(p => p.provider_type === providerSelectedId) ?? null,
    [providers, providerSelectedId],
  );

  const providerModels = useMemo(() => providerModelsData?.models ?? [], [providerModelsData]);

  // Provider list search (the list is small — search only, no chips/pagination;
  // the high-value target is the models table below).
  const {
    filteredItems: filteredProviders,
    searchValue: providerSearchValue,
    onSearchChange: providerOnSearchChange,
  } = useFilteredList({ items: providers, searchFn: providerSearchFn });

  // Models table (grows large): tier chips (state below) → search → paginate.
  // Sorted newest-first (by updated_at, fallback created_at) so a just-added or
  // just-edited entry lands on page 1 rather than being buried on a later page
  // once the seeded global catalog exceeds one page.
  const tierFilteredModels = useMemo(() => {
    const scoped = modelTier === 'all' ? providerModels : providerModels.filter(e => entryTier(e) === modelTier);
    return [...scoped].sort((a, b) => {
      const at = Date.parse(a.updated_at ?? a.created_at ?? '') || 0;
      const bt = Date.parse(b.updated_at ?? b.created_at ?? '') || 0;
      return bt - at;
    });
  }, [providerModels, modelTier]);
  const {
    filteredItems: searchedModels,
    searchValue: modelSearchValue,
    onSearchChange: modelOnSearchChange,
  } = useFilteredList({ items: tierFilteredModels, searchFn: modelSearchFn });
  const {
    page: modelPage,
    totalPages: modelTotalPages,
    pageSize: modelPageSize,
    startIndex: modelStartIndex,
    endIndex: modelEndIndex,
    onPageChange: onModelPageChange,
    reset: resetModelPage,
  } = usePagination({
    totalItems: searchedModels.length,
    pageSize: 25,
    pageSizeOptions: [25, 50, 100],
  });
  useEffect(() => {
    resetModelPage();
  }, [modelSearchValue, modelTier, providerSelectedId, resetModelPage]);
  // URL `model=` (Find a replacement / deep-link) must land on the page that
  // actually renders that row — pagination otherwise hides it.
  useEffect(() => {
    if (activeTab !== 'providers') return;
    if (!expandedModel || searchedModels.length === 0) return;
    const idx = searchedModels.findIndex(m => m.name === expandedModel);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / modelPageSize);
    if (targetPage !== modelPage) onModelPageChange(targetPage);
  }, [activeTab, expandedModel, searchedModels, modelPage, modelPageSize, onModelPageChange]);
  const pagedModels = searchedModels.slice(modelStartIndex, modelEndIndex);

  // Sync the provider-params editing draft when the selected provider changes
  // (and after mutations refetch the providers query — fresh server state
  // replaces the draft, mirroring the group edit-fields sync above).
  const selectedProviderParams = selectedProvider?.provider_params;
  useEffect(() => {
    setProviderParamsDraft(selectedProviderParams ?? EMPTY_PROVIDER_PARAMS);
  }, [selectedProviderParams]);

  // Clear the sync / discovery results when switching providers
  useEffect(() => {
    setSyncResult(null);
    setDiscoverSummary(null);
    setDiscoverDialogOpen(false);
  }, [providerSelectedId]);


  // Providers tab callbacks
  const getProviderItemId = useCallback((item: ProviderInfo) => item.provider_type, []);
  const handleProviderItemClick = useCallback((item: ProviderInfo) => {
    writeUrl(next => {
      next.set('tab', 'providers');
      next.set('provider', item.provider_type);
      next.delete('model');
    });
  }, [writeUrl]);

  const renderProviderItem = useCallback((item: ProviderInfo) => (
    <div
      data-testid={`provider-list-item-${item.provider_type}`}
      className={cn(
        "flex items-center gap-3 rounded-lg p-3 pr-20 transition-colors hover:bg-accent/50 cursor-pointer",
        providerSelectedId === item.provider_type && "bg-accent",
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-label font-semibold">
        {item.model_count}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-label font-medium">{formatLabel(item.provider_type)}</span>
          {credentialBadge(item)}
        </div>
        <p className="truncate text-caption text-muted-foreground">
          {item.model_count} model{item.model_count !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  ), [providerSelectedId]);

  const handleSaveProviderParams = useCallback(() => {
    if (!selectedProvider) return;
    setProviderSchemaError('');
    setProviderParamsMutation.mutate(
      { providerType: selectedProvider.provider_type, params: providerParamsDraft },
      {
        onSuccess: () => toast.success('Provider parameters saved'),
        onError: (err) => {
          // Narrowing rejection names the affected agents; surface it
          // in the schema card (verbatim), not only as a toast.
          setProviderSchemaError(err.message);
          toast.error(err.message);
        },
      },
    );
  }, [selectedProvider, providerParamsDraft, setProviderParamsMutation]);

  const handleSyncCatalog = useCallback(() => {
    setSyncResult(null);
    syncCatalogMutation.mutate(undefined, {
      onSuccess: (result) => setSyncResult(result),
      onError: (err) => toast.error(err.message),
    });
  }, [syncCatalogMutation]);

  const handleDiscoverImported = useCallback((imported: number) => {
    setDiscoverSummary(
      imported > 0
        ? `Imported ${imported} model${imported === 1 ? '' : 's'} into this account.`
        : 'No models imported.',
    );
    if (imported > 0) toast.success(`Imported ${imported} model${imported === 1 ? '' : 's'}`);
  }, []);

  const closeEntryDialog = useCallback(() => {
    setEntryDialogMode(null);
    setEntryDialogSource(undefined);
  }, []);


  // Provider detail content
  const providerDetailContent = useMemo(() => {
    if (!selectedProvider) return undefined;
    const globalCount = providerModels.filter(e => entryTier(e) === 'global').length;
    const accountCount = providerModels.length - globalCount;
    const tierFilters: Array<{ key: 'all' | ModelTier; label: string; count: number }> = [
      { key: 'all', label: 'All', count: providerModels.length },
      { key: 'global', label: 'Global', count: globalCount },
      { key: 'account', label: 'Account', count: accountCount },
    ];
    return (
      <div className="flex flex-col gap-4 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-title-2 font-semibold">{formatLabel(selectedProvider.provider_type)}</h2>
          <div className="flex items-center gap-2">
            {credentialBadge(selectedProvider)}
          </div>
        </div>
        <p className="text-label text-muted-foreground">
          {selectedProvider.credential_type === 'device'
            ? 'Credentials are provided by the executing device.'
            : selectedProvider.credential_type === 'none'
              ? 'No credential needed — this provider serves scripted, deterministic responses.'
              : selectedProvider.credential_configured
              ? 'Platform API key configured via environment.'
              : 'No platform API key configured — model calls fail unless an account key is set below.'}
        </p>

        {/* Provider BYOK key management — gated on the backend's data-driven
            capability flag, admins only. */}
        {selectedProvider.byok_supported && isAccountAdmin && (
          <ProviderKeySection provider={selectedProvider.provider_type} />
        )}

        {/*
          Provider-tier DEFAULT VALUES vs capability SPEC. The split is
          conceptual — preference vs constraint — not a permission boundary.
          Both cards are open to any member.
        */}
        <div
          data-testid="provider-defaults-card"
          className="rounded-md bg-card p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-label font-semibold">Default parameters</h3>
            <Button
              size="sm"
              data-testid="provider-defaults-edit"
              onClick={() => setProviderDefaultsOpen(true)}
            >
              Edit defaults
            </Button>
          </div>
          <p className="text-caption text-muted-foreground">
            Values agents inherit when they do not override a parameter. Changing one
            reaches every agent that has not pinned it, on its next turn.
          </p>
        </div>

        <details
          data-testid="provider-schema-card"
          className="rounded-md bg-card p-3"
        >
          <summary className="cursor-pointer text-label font-semibold">
            Parameter schema
          </summary>
          <div className="mt-2 flex items-center justify-between mb-2">
            <p className="text-caption text-muted-foreground">
              Bounds, types and enums for this provider. Narrowing a bound below a
              pinned agent value is rejected at save, naming the affected agents.
            </p>
            <Button
              size="sm"
              onClick={handleSaveProviderParams}
              disabled={setProviderParamsMutation.isPending}
            >
              {setProviderParamsMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
          <ParamSpecEditor
            params={providerParamsDraft}
            onChange={next => {
              setProviderSchemaError('');
              setProviderParamsDraft(next);
            }}
            disabled={setProviderParamsMutation.isPending}
            narrowingError={providerSchemaError || undefined}
          />
        </details>

        {/* Model entries */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-label font-semibold">Models ({providerModels.length})</h3>
            <div className="flex items-center gap-2">
              {selectedProvider && (
                selectedProvider.provider_type === 'test' ? (
                  <span className="text-caption text-muted-foreground" data-testid="provider-discover-hint">
                    The test provider's models are built in; there is nothing to discover.
                  </span>
                ) : selectedProvider.provider_type === 'lmstudio' && platform.type !== 'desktop' ? (
                  <span className="text-caption text-muted-foreground" data-testid="provider-discover-hint">
                    LM Studio discovery runs from the desktop app.
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="provider-discover"
                    title="Fetch this provider's live model list and import selected models as managed account entries"
                    onClick={() => { setDiscoverSummary(null); setDiscoverDialogOpen(true); }}
                  >
                    Discover…
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                data-testid="provider-sync"
                title="Refresh this account's Global entries from the seeded catalog (writes to the account, never the global master)"
                onClick={handleSyncCatalog}
                disabled={syncCatalogMutation.isPending}
              >
                {syncCatalogMutation.isPending ? 'Syncing...' : 'Sync Global'}
              </Button>
              <Button
                size="sm"
                data-testid="provider-add-model"
                onClick={() => {
                  setEntryDialogSource(undefined);
                  setEntryDialogMode('add');
                }}
              >
                + Add Model
              </Button>
            </div>
          </div>
          <div className="mb-2 flex items-center gap-2" data-testid="provider-tier-filter">
            {tierFilters.map(f => (
              <Button
                key={f.key}
                variant={modelTier === f.key ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 text-caption px-2"
                data-testid={`provider-tier-filter-${f.key}`}
                onClick={() => setModelTier(f.key)}
              >
                {f.label} ({f.count})
              </Button>
            ))}
            <span className="ml-auto text-micro text-muted-foreground">
              Global entries are read-only mirrors of the seeded catalog — Copy one to manage it in this account.
            </span>
          </div>
          <div className="mb-2">
            <Input
              value={modelSearchValue}
              onChange={(e) => modelOnSearchChange(e.target.value)}
              placeholder="Search models…"
              data-testid="provider-model-search"
              className="h-8 text-caption"
            />
          </div>
          {syncResult && (
            <p className="mb-2 text-caption text-muted-foreground" data-testid="provider-sync-result">
              Sync complete: {syncResult.inserted} inserted, {syncResult.updated} updated, {syncResult.removed} removed
              {syncResult.blocked.length > 0 && (
                <> — {syncResult.blocked.length} blocked (in use): {syncResult.blocked.map(b => `${b.provider_type}/${b.name}`).join(', ')}</>
              )}
            </p>
          )}
          {discoverSummary && (
            <p className="mb-2 text-caption text-muted-foreground" data-testid="provider-discover-result">
              {discoverSummary}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-label">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Display Name</th>
                  <th className="px-3 py-2 text-left font-medium">Entry Name</th>
                  <th className="px-3 py-2 text-left font-medium">Model ID</th>
                  <th className="px-3 py-2 text-right font-medium">Context</th>
                  <th className="px-3 py-2 text-right font-medium">Max Output</th>
                  <th className="px-3 py-2 text-left font-medium">Capabilities</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Origin</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {searchedModels.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-4 text-center text-caption text-muted-foreground">
                      {modelSearchValue
                        ? 'No models match your search'
                        : modelTier === 'all'
                          ? 'No model entries for this provider'
                          : `No ${modelTier} model entries for this provider`}
                    </td>
                  </tr>
                )}
                {pagedModels.map(entry => (
                  <Fragment key={entry.name}>
                  <tr className="border-b last:border-0" data-testid={`provider-model-row-${entry.name}`}>
                    <td className="px-3 py-2 text-caption whitespace-nowrap">{entry.display_name || entry.name}</td>
                    <td className="px-3 py-2 font-mono text-caption whitespace-nowrap">
                      <button
                        type="button"
                        data-testid={`provider-model-expand-${entry.name}`}
                        aria-expanded={expandedModel === entry.name}
                        onClick={() => writeUrl(next => {
                          if (expandedModel === entry.name) next.delete('model');
                          else next.set('model', entry.name);
                        })}
                        className="text-left hover:underline"
                      >
                        {expandedModel === entry.name ? '▾ ' : '▸ '}
                        {entry.name}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-caption whitespace-nowrap text-muted-foreground">{entry.model_id}</td>
                    <td className="px-3 py-2 text-caption text-right whitespace-nowrap" title={entry.context_window == null ? 'The provider did not report a context window — set one to make this model selectable.' : undefined}>{entry.context_window != null ? entry.context_window.toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 text-caption text-right whitespace-nowrap">{entry.max_output_tokens != null ? entry.max_output_tokens.toLocaleString() : '—'}</td>
                    {/*
                      Capability + lifecycle metadata the catalog already stored and
                      discarded. Tri-state: an `undefined` flag renders
                      NOTHING — never a negative.
                    */}
                    <td className="px-3 py-2"><ModelCapabilityPills flags={entry} /></td>
                    <td className="px-3 py-2">
                      <ModelStatusBadge status={entry.status} deprecationDate={entry.deprecation_date} />
                    </td>
                    <td className="px-3 py-2">{originBadge(entry)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-caption px-2"
                          onClick={() => {
                            setEntryDialogSource(entry);
                            setEntryDialogMode('copy');
                          }}
                        >
                          Copy
                        </Button>
                        {/*
                          Default VALUES, separate from the capability SPEC that
                          "Edit" opens. Open
                          to any member — the split is conceptual, a preference
                          versus a constraint, not a permission boundary.
                          Available on Global entries too: a default is written
                          against the ACCOUNT's resolved schema, so it never
                          touches the global master.
                        */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-caption px-2"
                          data-testid={`provider-model-defaults-${entry.name}`}
                          title="Set the values agents inherit when they do not override a parameter"
                          onClick={() => setDefaultsTarget(entry)}
                        >
                          Defaults
                        </Button>
                        {entryTier(entry) === 'account' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-caption px-2"
                              onClick={() => {
                                setEntryDialogSource(entry);
                                setEntryDialogMode('edit');
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-caption px-2 text-destructive hover:text-destructive"
                              onClick={() => setDeleteEntryTarget(entry)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedModel === entry.name && (
                    <tr
                      className="border-b bg-muted/30 last:border-0"
                      data-testid={`provider-model-detail-${entry.name}`}
                    >
                      <td colSpan={9} className="px-3 py-2">
                        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-caption">
                          <dt className="text-muted-foreground">Model ID</dt>
                          <dd className="font-mono" data-testid="provider-model-detail-model-id">{entry.model_id}</dd>
                          <dt className="text-muted-foreground">Modalities</dt>
                          <dd>
                            <ModelModalities input={entry.input_modalities} output={entry.output_modalities} /> 
                          </dd>
                          <dt className="text-muted-foreground">Pricing</dt>
                          <dd><ModelPricingCell pricing={entry.pricing} /></dd>
                          <dt className="text-muted-foreground">Source</dt>
                          <dd className="font-mono" data-testid="provider-model-detail-source">{entry.source_api ?? '—'}</dd>
                          <dt className="text-muted-foreground">Metadata synced</dt>
                          <dd data-testid="provider-model-detail-synced">{entry.metadata_synced_at ?? '—'}</dd>
                        </dl>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {modelTotalPages > 1 && (
            <div
              className="mt-2 flex items-center justify-between text-caption text-muted-foreground"
              data-testid="provider-model-pagination"
            >
              <span>{searchedModels.length} models · Page {modelPage + 1} of {modelTotalPages}</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-caption px-2"
                  data-testid="provider-model-prev"
                  disabled={modelPage === 0}
                  onClick={() => onModelPageChange(modelPage - 1)}
                >
                  Prev
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-caption px-2"
                  data-testid="provider-model-next"
                  disabled={modelPage >= modelTotalPages - 1}
                  onClick={() => onModelPageChange(modelPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
        {selectedProvider && (
          <ProviderDiscoverDialog
            provider={selectedProvider.provider_type}
            open={discoverDialogOpen}
            onClose={() => setDiscoverDialogOpen(false)}
            getDeviceId={async () => {
              const { getDeviceIdAsync } = await import('../services/sdk-instance');
              return getDeviceIdAsync();
            }}
            onImported={handleDiscoverImported}
          />
        )}
      </div>
    );
  }, [selectedProvider, isAccountAdmin, providerParamsDraft, providerSchemaError, providerModels, modelTier, searchedModels, pagedModels, modelSearchValue, modelOnSearchChange, modelPage, modelTotalPages, onModelPageChange, expandedModel, writeUrl, syncResult, discoverSummary, discoverDialogOpen, handleSaveProviderParams, handleSyncCatalog, handleDiscoverImported, platform, setProviderParamsMutation.isPending, syncCatalogMutation.isPending]);

  const detailContent = useMemo(() => {
    // Agents + Processes are full-width libraries that navigate to standalone
    // editor routes — they never open a detail panel. Only Tools and Providers
    // use the right-hand detail panel.
    if (activeTab === 'tools') return mcpPanel.detail;
    if (activeTab === 'providers') return providerDetailContent;
    return undefined;
  }, [activeTab, mcpPanel.detail, providerDetailContent]);

  // Compute selectedId and onSelectItem for ListDetailLayout based on active tab
  const layoutSelectedId = useMemo(() => {
    if (activeTab === 'tools') return mcpPanel.selectedId;
    if (activeTab === 'providers') return providerSelectedId;
    return null; // agents/processes are full-width libraries, no detail panel
  }, [activeTab, mcpPanel.selectedId, providerSelectedId]);

  const handleLayoutSelectItem = useCallback((id: string | null) => {
    if (activeTab === 'tools') {
      selectMcp(id);
    } else if (activeTab === 'providers') {
      writeUrl(next => {
        if (id) next.set('provider', id);
        else next.delete('provider');
        next.delete('model');
      });
    }
  }, [activeTab, writeUrl, selectMcp]);

  return (
    <div className="flex h-full flex-col">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col">
      <PageHeaderBar
        tabs={
          <TabsList aria-label="Agents" className={PAGE_TABS_LIST_CLASS}>
            <TabsTrigger value="personas" data-testid="agents-tab-personas" className={PAGE_TABS_TRIGGER_CLASS}>Personas</TabsTrigger>
            <TabsTrigger value="agents" data-testid="agents-tab-agents" className={PAGE_TABS_TRIGGER_CLASS}>Agents</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tools-tab-trigger" className={PAGE_TABS_TRIGGER_CLASS}>Tools</TabsTrigger>
            <TabsTrigger value="providers" data-testid="providers-tab-trigger" className={PAGE_TABS_TRIGGER_CLASS}>Providers</TabsTrigger>
            <TabsTrigger value="processes" data-testid="agents-tab-processes" className={PAGE_TABS_TRIGGER_CLASS}>Processes</TabsTrigger>
            <TabsTrigger value="context" data-testid="agents-tab-context" className={PAGE_TABS_TRIGGER_CLASS}>Context</TabsTrigger>
            <TabsTrigger value="skills" data-testid="agents-tab-skills" className={PAGE_TABS_TRIGGER_CLASS}>Skills</TabsTrigger>
          </TabsList>
        }
      />

      <ListDetailLayout
        layout="panel"
        panelWidth="65%"
        selectedId={layoutSelectedId}
        onSelectItem={handleLayoutSelectItem}
        className="flex-1 min-h-0"
        listContent={
          <div className="flex h-full flex-col">
              <TabsContent value="personas" className="p-4">
                <AgentLibrary />
              </TabsContent>
              <TabsContent value="agents" className="p-4">
                <AgentRoster />
              </TabsContent>
              <TabsContent value="context" className="p-4">
                <ContextTab />
              </TabsContent>
              <TabsContent value="skills" className="p-4">
                <SkillsTab />
              </TabsContent>
              <TabsContent value="tools" className="p-4">
                {mcpPanel.list}
              </TabsContent>
              <TabsContent value="providers" className="p-4">
                {/*
                  Stack-wide, so it sits above the provider list rather than
                  inside one provider's detail panel — the model belongs to the
                  platform, not to whichever provider happens to be selected.
                */}
                <div className="mb-4">
                  <VisionModelNotice
                    info={visionModelInfo}
                    isLoading={visionModelLoading}
                    error={visionModelError}
                  />
                </div>
                <DataList<ProviderInfo>
                  items={filteredProviders}
                  renderItem={renderProviderItem}
                  searchValue={providerSearchValue}
                  onSearchChange={providerOnSearchChange}
                  searchPlaceholder="Search providers..."
                  getItemId={getProviderItemId}
                  onItemClick={handleProviderItemClick}
                  isLoading={providersLoading}
                  emptyState={
                    <div className="text-center">
                      <p className="text-body text-muted-foreground">
                        {providersError
                          ? 'Failed to load providers. Make sure the backend service is running.'
                          : 'No providers available'}
                      </p>
                    </div>
                  }
                />
              </TabsContent>
              <TabsContent value="processes" className="p-4">
                <AgentProcessLibrary />
              </TabsContent>
          </div>
        }
        detailContent={detailContent}
      />
      </Tabs>

      {/* Model catalog entry dialog (add / copy / edit) */}
      {selectedProvider && entryDialogMode && (
        <ModelEntryDialog
          mode={entryDialogMode}
          providerType={selectedProvider.provider_type}
          source={entryDialogSource}
          providerParams={selectedProvider.provider_params}
          open={entryDialogMode !== null}
          onClose={closeEntryDialog}
        />
      )}

      {/* Model DEFAULT VALUES dialog — separate route, separate blob,
          and survives a catalog Sync, unlike the capability spec above. */}
      {selectedProvider && defaultsTarget && (
        <ModelDefaultsDialog
          providerType={selectedProvider.provider_type}
          modelName={defaultsTarget.name}
          open={defaultsTarget !== null}
          onClose={() => setDefaultsTarget(null)}
        />
      )}

      {/* Provider-tier DEFAULT VALUES — open to any member. */}
      {selectedProvider && providerDefaultsOpen && (
        <ProviderDefaultsDialog
          providerType={selectedProvider.provider_type}
          open={providerDefaultsOpen}
          onClose={() => setProviderDefaultsOpen(false)}
        />
      )}

      {/* Delete model catalog entry dialog */}
      {selectedProvider && deleteEntryTarget && (
        <DeleteEntryDialog
          providerType={selectedProvider.provider_type}
          entry={deleteEntryTarget}
          open={deleteEntryTarget !== null}
          onClose={() => setDeleteEntryTarget(null)}
          onDeleted={() => toast.success('Model entry deleted')}
        />
      )}

      {mcpPanel.dialogs}
    </div>
  );
}
